import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { buildFullName, fetchProfileRemote } from "@/lib/authApi";
import { toastApiError } from "@/lib/toastApiError";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AudioUploadField } from "./AudioUploadField";
import { ImageUploadField } from "./ImageUploadField";
import { Switch } from "@/components/ui/switch";
import { RichTextField } from "@/components/RichTextField";
import { RelatedTermsPicker } from "./RelatedTermsPicker";
import type { Term } from "../types";

interface FormData {
  word: string;
  word_gujarati: string;
  word_devanagari: string;
  word_hindi: string;
  meaning_english: string;
  meaning_hindi: string;
  meaning_gujarati: string;
  pronunciation: string;
  example_usage: string;
  status: "pending" | "approved" | "rejected";
  admin_notes: string;
  contributor_name: string;
  contributor_email: string;
  audio_url: string;
  image_url: string;
  featured: boolean;
  /** When false, approved terms are hidden from browse/search/detail for visitors. */
  is_enabled: boolean;
  /** Curated related-term UUIDs, in chosen order. */
  related_term_ids: string[];
}

const CONTRIBUTOR_DISABLED_INPUT_CLASSES = cn(
  "bg-muted/35 border-border/60 opacity-95",
  "disabled:cursor-not-allowed disabled:opacity-95"
);

function contributorNameFromProfile(
  profile: {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null
): string {
  if (!profile) return "";
  const full = profile.full_name?.trim();
  if (full) return full;
  const fromParts = buildFullName(
    profile.first_name ?? "",
    profile.last_name ?? ""
  ).trim();
  return fromParts;
}

function fallbackNameFromEmail(email: string): string {
  const at = email.indexOf("@");
  const local =
    at > 0 ? email.slice(0, at).replace(/\./g, " ").trim() : email.trim();
  return local.length > 0 ? local : "Staff";
}

function ContributorFieldsReadOnly(props: {
  name: string;
  email: string;
  subtitle: string;
}) {
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs">{props.subtitle}</p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2">
          <Label>Contributor Name</Label>
          <Input
            value={props.name || "—"}
            disabled
            placeholder="Contributor name"
            autoComplete="name"
            className={CONTRIBUTOR_DISABLED_INPUT_CLASSES}
          />
        </div>
        <div className="grid gap-2">
          <Label>Contributor Email</Label>
          <Input
            value={props.email || "—"}
            type="email"
            disabled
            placeholder="Contributor email"
            autoComplete="email"
            className={CONTRIBUTOR_DISABLED_INPUT_CLASSES}
          />
        </div>
      </div>
    </div>
  );
}

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: FormData;
  onFormChange: (data: FormData) => void;
  pendingAudioFile: File | null;
  onPendingAudioFileChange: (file: File | null) => void;
  pendingImageFile: File | null;
  onPendingImageFileChange: (file: File | null) => void;
  markedForImageRemoval: boolean;
  onRemoveImage: () => void;
  onUndoRemoveImage: () => void;
  onSave: () => void;
  /** Candidate terms for the related-terms picker. */
  allTerms: Term[];
  /** Id of the term being edited (excluded from its own related list). */
  editingId?: string;
}

export function EditDialog({
  open,
  onOpenChange,
  formData,
  onFormChange,
  pendingAudioFile,
  onPendingAudioFileChange,
  pendingImageFile,
  onPendingImageFileChange,
  markedForImageRemoval,
  onRemoveImage,
  onUndoRemoveImage,
  onSave,
  allTerms,
  editingId,
}: EditDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Term</DialogTitle>
          <DialogDescription>Make changes to the term.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Word</Label>
            <Input
              value={formData.word}
              onChange={e =>
                onFormChange({ ...formData, word: e.target.value })
              }
            />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="grid gap-2">
              <Label>Word (Gujarati)</Label>
              <Input
                value={formData.word_gujarati}
                onChange={e =>
                  onFormChange({ ...formData, word_gujarati: e.target.value })
                }
                placeholder="ગુજરાતી..."
                className="font-devanagari"
              />
            </div>
            <div className="grid gap-2">
              <Label>Word (Devanagari)</Label>
              <Input
                value={formData.word_devanagari}
                onChange={e =>
                  onFormChange({ ...formData, word_devanagari: e.target.value })
                }
                placeholder="देवनागरी..."
                className="font-devanagari"
              />
            </div>
            <div className="grid gap-2">
              <Label>Word (Hindi)</Label>
              <Input
                value={formData.word_hindi}
                onChange={e =>
                  onFormChange({ ...formData, word_hindi: e.target.value })
                }
                placeholder="हिंदी..."
                className="font-devanagari"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Meaning (English)</Label>
            <RichTextField
              value={formData.meaning_english}
              onChange={html =>
                onFormChange({ ...formData, meaning_english: html })
              }
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label>Meaning (Hindi)</Label>
              <RichTextField
                value={formData.meaning_hindi}
                onChange={html =>
                  onFormChange({ ...formData, meaning_hindi: html })
                }
                indic
              />
            </div>
            <div className="grid gap-2">
              <Label>Meaning (Gujarati)</Label>
              <RichTextField
                value={formData.meaning_gujarati}
                onChange={html =>
                  onFormChange({ ...formData, meaning_gujarati: html })
                }
                indic
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label>Pronunciation</Label>
              <Input
                value={formData.pronunciation}
                onChange={e =>
                  onFormChange({ ...formData, pronunciation: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: "pending" | "approved" | "rejected") =>
                  onFormChange({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Example Usage</Label>
            <Textarea
              value={formData.example_usage}
              onChange={e =>
                onFormChange({ ...formData, example_usage: e.target.value })
              }
              placeholder="Example sentence or phrase"
              className="min-h-[80px]"
            />
          </div>
          <div className="grid gap-2">
            <Label>Related Terms</Label>
            <p className="text-muted-foreground text-xs">
              Link this term to other words. They appear as clickable related
              terms on the public page.
            </p>
            <RelatedTermsPicker
              allTerms={allTerms}
              value={formData.related_term_ids}
              onChange={ids =>
                onFormChange({ ...formData, related_term_ids: ids })
              }
              excludeId={editingId}
            />
          </div>
          <ContributorFieldsReadOnly
            name={formData.contributor_name}
            email={formData.contributor_email}
            subtitle="Original submission — contributor details are read-only."
          />
          <AudioUploadField
            remoteUrl={formData.audio_url?.trim() || undefined}
            pendingFile={pendingAudioFile}
            onPendingFileChange={onPendingAudioFileChange}
          />
          <ImageUploadField
            remoteUrl={formData.image_url?.trim() || undefined}
            pendingFile={pendingImageFile}
            onPendingFileChange={onPendingImageFileChange}
            markedForRemoval={markedForImageRemoval}
            onRemove={onRemoveImage}
            onUndoRemove={onUndoRemoveImage}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.featured}
              onCheckedChange={checked =>
                onFormChange({ ...formData, featured: !!checked })
              }
            />
            <Label>Featured Word</Label>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="edit-is-enable" className="text-base">
                Visible on site
              </Label>
              <p className="text-muted-foreground text-xs">
                Approved terms stay hidden from the public lexicon while off.
              </p>
            </div>
            <Switch
              id="edit-is-enable"
              checked={formData.is_enabled}
              onCheckedChange={checked =>
                onFormChange({ ...formData, is_enabled: !!checked })
              }
            />
          </div>
          <div className="grid gap-2">
            <Label>Admin Notes</Label>
            <Textarea
              value={formData.admin_notes}
              onChange={e =>
                onFormChange({ ...formData, admin_notes: e.target.value })
              }
              placeholder="Internal notes (not visible to public)"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} className="bg-primary hover:bg-primary/90">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wordName: string;
  onDelete: () => void;
}

export function DeleteDialog({
  open,
  onOpenChange,
  wordName,
  onDelete,
}: DeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Term</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{wordName}"? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onDelete}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: FormData;
  onFormChange: (data: FormData) => void;
  pendingAudioFile: File | null;
  onPendingAudioFileChange: (file: File | null) => void;
  pendingImageFile: File | null;
  onPendingImageFileChange: (file: File | null) => void;
  onAdd: () => void;
  /** Candidate terms for the related-terms picker. */
  allTerms: Term[];
}

export function AddDialog({
  open,
  onOpenChange,
  formData,
  onFormChange,
  pendingAudioFile,
  onPendingAudioFileChange,
  pendingImageFile,
  onPendingImageFileChange,
  onAdd,
  allTerms,
}: AddDialogProps) {
  const { staffUser } = useAuth();
  const formSnapRef = useRef(formData);
  formSnapRef.current = formData;

  useEffect(() => {
    if (!open || !staffUser?.id) {
      return;
    }
    let cancelled = false;
    const fbEmail = staffUser.email?.trim() ?? "";

    const apply = (contributor_email: string, contributor_name: string) => {
      if (cancelled) {
        return;
      }
      onFormChange({
        ...formSnapRef.current,
        contributor_email,
        contributor_name,
      });
    };

    const fallbackName = fallbackNameFromEmail(fbEmail);
    apply(fbEmail, fallbackName);

    void (async () => {
      const { profile, error } = await fetchProfileRemote(
        staffUser.id,
        "staff"
      );
      if (cancelled) {
        return;
      }
      if (error) {
        toastApiError(error);
        return;
      }
      const email = profile?.email?.trim() || fbEmail;
      let name = contributorNameFromProfile(profile);
      if (!name.trim()) {
        name = fallbackNameFromEmail(email);
      }
      apply(email, name);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- merge only when dialog opens / staff changes
  }, [open, staffUser?.id, staffUser?.email]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Term</DialogTitle>
          <DialogDescription>Add a new term to the lexicon.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Word *</Label>
            <Input
              value={formData.word}
              onChange={e =>
                onFormChange({ ...formData, word: e.target.value })
              }
              placeholder="Enter the term"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="grid gap-2">
              <Label>Word (Gujarati)</Label>
              <Input
                value={formData.word_gujarati}
                onChange={e =>
                  onFormChange({ ...formData, word_gujarati: e.target.value })
                }
                placeholder="ગુજરાતી..."
                className="font-devanagari"
              />
            </div>
            <div className="grid gap-2">
              <Label>Word (Devanagari)</Label>
              <Input
                value={formData.word_devanagari}
                onChange={e =>
                  onFormChange({ ...formData, word_devanagari: e.target.value })
                }
                placeholder="देवनागरी..."
                className="font-devanagari"
              />
            </div>
            <div className="grid gap-2">
              <Label>Word (Hindi)</Label>
              <Input
                value={formData.word_hindi}
                onChange={e =>
                  onFormChange({ ...formData, word_hindi: e.target.value })
                }
                placeholder="हिंदी..."
                className="font-devanagari"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Meaning (English)</Label>
            <RichTextField
              value={formData.meaning_english}
              onChange={html =>
                onFormChange({ ...formData, meaning_english: html })
              }
              placeholder="Write meaning in English..."
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label>Meaning (Hindi)</Label>
              <RichTextField
                value={formData.meaning_hindi}
                onChange={html =>
                  onFormChange({ ...formData, meaning_hindi: html })
                }
                placeholder="हिंदी में अर्थ..."
                indic
              />
            </div>
            <div className="grid gap-2">
              <Label>Meaning (Gujarati)</Label>
              <RichTextField
                value={formData.meaning_gujarati}
                onChange={html =>
                  onFormChange({ ...formData, meaning_gujarati: html })
                }
                placeholder="ગુજરાતીમાં અર્થ..."
                indic
              />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label>Pronunciation</Label>
              <Input
                value={formData.pronunciation}
                onChange={e =>
                  onFormChange({ ...formData, pronunciation: e.target.value })
                }
                placeholder="e.g., ah-HIM-sah"
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: "pending" | "approved" | "rejected") =>
                  onFormChange({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">
                    Approved (Publish Now)
                  </SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Example Usage</Label>
            <Textarea
              value={formData.example_usage}
              onChange={e =>
                onFormChange({ ...formData, example_usage: e.target.value })
              }
              placeholder="How the term is used..."
              className="min-h-[80px]"
            />
          </div>
          <div className="grid gap-2">
            <Label>Related Terms</Label>
            <p className="text-muted-foreground text-xs">
              Link this term to other words. They appear as clickable related
              terms on the public page.
            </p>
            <RelatedTermsPicker
              allTerms={allTerms}
              value={formData.related_term_ids}
              onChange={ids =>
                onFormChange({ ...formData, related_term_ids: ids })
              }
            />
          </div>
          <ContributorFieldsReadOnly
            name={formData.contributor_name}
            email={formData.contributor_email}
            subtitle=""
          />
          <AudioUploadField
            remoteUrl={undefined}
            pendingFile={pendingAudioFile}
            onPendingFileChange={onPendingAudioFileChange}
          />
          <ImageUploadField
            remoteUrl={undefined}
            pendingFile={pendingImageFile}
            onPendingFileChange={onPendingImageFileChange}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.featured}
              onCheckedChange={checked =>
                onFormChange({ ...formData, featured: !!checked })
              }
            />
            <Label>Featured Word</Label>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-3 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="add-is-enable" className="text-base">
                Visible on site
              </Label>
             
            </div>
            <Switch
              id="add-is-enable"
              checked={formData.is_enabled}
              onCheckedChange={checked =>
                onFormChange({ ...formData, is_enabled: !!checked })
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onAdd}
            className="bg-primary hover:bg-primary/90"
            disabled={!formData.word}
          >
            Add Term
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
